/**
 * The axis cell's context menu, wired up: {@link computeAxisCellContextMenu} in
 * `store/logic/AxisCellContextMenuLogic` decides which options the menu lists, how they read and
 * which of them are hidden or disabled, and this module attaches each option's handler.
 *
 * The handlers are the only impure part of the menu — they open the rename modal, log analytics and
 * read the current user — which is why they live here in the UI layer rather than with the compute.
 *
 * {@link useAxisCellContextMenu} is what `TableAxisCell` renders from;
 * {@link getAxisCellContextMenuOptions} is the same thing without the React plumbing, so the wiring
 * can be unit tested directly.
 */

import reactUpdate from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as contextmenu from 'components/ContextMenu';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {
  logColumnAnalytics,
  StructuredTableAnalyticsParams,
} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import StructuredTableContext from 'notebook/structured-tables/util/StructuredTableContext';
import type {InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import {USER_CREATED_COLUMN_NAME_MAX_LENGTH} from 'notebook/tables/constants/TableColumnConstants';
import {SelectionRange, TABLE_ROW, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
// Imported for its types only, so that this module and the component it serves do not depend on
// each other at runtime.
import type {TableAxisCellProps} from 'notebook/tables/core-ui/TableAxisCell';
import {useAxisCellModel} from 'notebook/tables/store/hooks/useAxisCellModel';
import {useTableProps} from 'notebook/tables/store/hooks/useTableProps';
import {
  AxisCellContextMenuInput,
  AxisCellMenuEntry,
  AxisCellMenuItem,
  AxisCellMenuItemEntry,
  computeAxisCellContextMenu,
  InsertDimDirection,
} from 'notebook/tables/store/logic/AxisCellContextMenuLogic';
import type {TableMutationMethods} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {AxisCellModel, isColumnHeaderModel} from 'notebook/tables/store/models/AxisCellModel';
import {FileAnalyticsParams} from 'notebook/util/analytics/FileAnalyticsUtils';
import EntityStore from 'stores/EntityStore';
import * as modals from 'util/Modals';
import {ElementMouseEvent} from 'util/Types';

type MenuOption =
  | '-' // specifically, a menu separator
  | {
      name: string;
      click: (e: ElementMouseEvent) => void;
      hide: () => boolean;
      disabled?: boolean;
    };

export interface AnalyticsParams {
  fileAnalyticsParams: FileAnalyticsParams | null;
  structuredTableAnalyticsParams: StructuredTableAnalyticsParams | null;
}

/**
 * When ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION is enabled, tenant admins can lock and unlock columns
 * via the context menu even in execution context (e.g. entries), where lock/unlock is normally hidden.
 */
export function canModifyColumnLockAsAdmin(): boolean {
  return (
    BenchlingConfig.ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION &&
    Boolean(EntityStore.currentUser()?.isTenantAdmin)
  );
}

export function hasVisibleContextOptions(
  isGhostCell: boolean,
  contextMenuOptions: Array<contextmenu.ContextMenuOption>
): boolean {
  // Ghost cells in plain tables should never show context options/menus.
  if (isGhostCell) {
    return false;
  }

  function filterFunc(option: string | MenuOption | contextmenu.ClickableContextMenuOption | null): boolean {
    return typeof option === 'object' && !_.result(option, 'hide');
  }

  return contextMenuOptions.some((option) => filterFunc(option));
}

/**
 * The dimension actions an axis cell's context menu can perform, narrowed to the ones the cell's
 * own model permits (see `getPermittedRowDimActions` / `getPermittedColumnDimActions`). An action
 * left undefined is not offered, and the option that performs it is hidden rather than disabled.
 */
export interface AxisCellDimActions {
  renameDim?: (index: number, name: string) => void;
  toggleHeader?: () => void;
  deleteTable?: () => void;
  delDim?: (minIndex: number | null, maxIndex: number | null) => void;
  insertDimBefore?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
  insertDimAfter?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
}

/** An axis cell rendered outside of a table, which permits none of the dimension actions. */
const NO_DIM_ACTIONS: AxisCellDimActions = {};

/**
 * The table-level inputs to an axis cell's context menu, which {@link TableAxisCell} reads from the
 * table model rather than having them threaded down as props. An axis cell rendered outside of a
 * table has none of them.
 */
export interface AxisCellContextMenuTableContext {
  /** The extra options the table contributes for this cell's selection. */
  extraOptions: Array<contextmenu.ContextMenuOption>;
  /**
   * The column names renaming must reject. Passed as a function rather than as an array because it
   * is only needed once the rename modal opens, so it must not be called during render.
   */
  getDisallowedColumnNames?: () => Array<string>;
  /** Whether the table is rendered in Single Input-only view. */
  isSingleInputView: boolean;
  /** Locks and unlocks the column, which is a change to its constraint. */
  setColumnConstraint?: TableMutationMethods['setColumnConstraint'];
}

/** The context menu of an axis cell rendered outside of a table, which the table contributes to. */
const NO_TABLE_CONTEXT: AxisCellContextMenuTableContext = {
  extraOptions: [],
  isSingleInputView: false,
};

/** The action each of the menu's built-in options performs when chosen. */
interface AxisCellMenuHandlers {
  renameDim: () => void;
  toggleHeader: () => void;
  deleteTable: () => void;
  deleteDim: (range: SelectionRange) => void;
  setColumnProtected: (isProtected: boolean) => void;
  freezeColumn: () => void;
  unfreezeColumn: () => void;
  insertDim: (
    direction: InsertDimDirection,
    range: SelectionRange,
    columnInfo: InsertableColumnInfo | {}
  ) => void;
}

/** Which side of the selection the given insert option inserts on. */
function getInsertDirection(item: AxisCellMenuItem): InsertDimDirection {
  return item === AxisCellMenuItem.INSERT_DIM_BEFORE ? 'BEFORE' : 'AFTER';
}

function isInsertItem(item: AxisCellMenuItem): boolean {
  return item === AxisCellMenuItem.INSERT_DIM_BEFORE || item === AxisCellMenuItem.INSERT_DIM_AFTER;
}

/** What the menu's inputs are for the given cell, from its model and the table it is rendered in. */
function getContextMenuInput(
  props: TableAxisCellProps,
  dimActions: AxisCellDimActions,
  axisCellModel: AxisCellModel,
  tableContext: AxisCellContextMenuTableContext
): AxisCellContextMenuInput {
  const canFreezeColumns = isColumnHeaderModel(axisCellModel) && axisCellModel.canFreezeColumns;
  return {
    axisCellModel,
    availableActions: {
      canRenameDim: dimActions.renameDim != null,
      canDeleteDim: dimActions.delDim != null,
      canDeleteTable: dimActions.deleteTable != null,
      canToggleHeader: dimActions.toggleHeader != null,
      canInsertDimBefore: dimActions.insertDimBefore != null,
      canInsertDimAfter: dimActions.insertDimAfter != null,
      canFreezeColumn: canFreezeColumns && props.freezeThroughColumn != null,
      canUnfreezeColumn: canFreezeColumns && props.unfreezeFromColumn != null,
    },
    extraOptions: [...tableContext.extraOptions, ...(props.additionalContextMenuOptions ?? [])],
    isSingleInputView: tableContext.isSingleInputView,
    canModifyColumnLockAsAdmin: canModifyColumnLockAsAdmin(),
  };
}

/**
 * The thin wiring between the menu's built-in options and the dimension actions they perform. Each
 * action is still a prop or a table mutation; only the modal and analytics side effects live here.
 */
function getContextMenuHandlers(
  props: TableAxisCellProps,
  dimActions: AxisCellDimActions,
  axisCellModel: AxisCellModel,
  tableContext: AxisCellContextMenuTableContext,
  analyticsParams?: AnalyticsParams | null
): AxisCellMenuHandlers {
  const {dim, index} = axisCellModel;
  const columnHeader = isColumnHeaderModel(axisCellModel) ? axisCellModel : null;
  const label = columnHeader?.label ?? null;
  const columnConstraint = columnHeader?.columnConstraint ?? null;

  return {
    renameDim: () => {
      const {getDisallowedColumnNames} = tableContext;
      const disallowedColumnNames = getDisallowedColumnNames ? getDisallowedColumnNames() : [];
      const validate = (name: string): boolean =>
        name === label ||
        (!disallowedColumnNames.includes(name) && name.length <= USER_CREATED_COLUMN_NAME_MAX_LENGTH);

      // A column label can be reset if it is explicitly set.
      let canReset = columnConstraint ? columnConstraint.canResetLabel : null;
      if (canReset == null) {
        canReset = label != null;
      }

      modals.prompt('Rename column', label, validate, {canReset}).then((name) => {
        if (dimActions.renameDim && name != null) {
          dimActions.renameDim(index, name);
        }
      });
    },

    toggleHeader: () => {
      dimActions.toggleHeader?.();
    },

    deleteTable: () => {
      dimActions.deleteTable?.();
    },

    deleteDim: (range) => {
      // TODO(T7364): Check that delDim is defined since the actions can change after the context
      // menu is opened.
      if (typeof dimActions.delDim === 'function') {
        dimActions.delDim(range.minIndex, range.maxIndex);
        if (dim === TableDimension.COLUMN && analyticsParams) {
          logColumnAnalytics({
            columnConstraint,
            analyticsEvent: ELN_ANALYTICS_ACTIONS.DELETE_COLUMN,
            structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
            fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
          });
        }
      }
    },

    setColumnProtected: (newIsProtected) => {
      const {setColumnConstraint} = tableContext;
      if (!setColumnConstraint) {
        return;
      }

      if (columnConstraint?.structuredTableColumn) {
        setColumnConstraint(
          index,
          reactUpdate(columnConstraint, {
            structuredTableColumn: {$merge: {isProtected: newIsProtected}},
            $merge: {isProtected: newIsProtected},
          }),
          {}
        );
      } else {
        setColumnConstraint(
          index,
          reactUpdate(columnConstraint || {}, {$merge: {isProtected: newIsProtected}}),
          {}
        );
      }
      if (analyticsParams) {
        logColumnAnalytics({
          columnConstraint,
          analyticsEvent: ELN_ANALYTICS_ACTIONS.TOGGLED_COLUMN_LOCKING,
          structuredTableAnalyticsParams: analyticsParams.structuredTableAnalyticsParams,
          fileAnalyticsParams: analyticsParams.fileAnalyticsParams,
          additionalMetadata: {
            setting: newIsProtected ? 'lock' : 'unlock',
          },
        });
      }
    },

    freezeColumn: () => {
      props.freezeThroughColumn?.(index);
    },

    unfreezeColumn: () => {
      props.unfreezeFromColumn?.(index);
    },

    insertDim: (direction, range, columnInfo) => {
      // TODO(T7364): Check that the insert action is defined since the actions can change after the
      // context menu is opened.
      const insert = direction === 'BEFORE' ? dimActions.insertDimBefore : dimActions.insertDimAfter;
      if (typeof insert === 'function') {
        insert(range.minIndex, range.maxIndex, columnInfo);
      }
    },
  };
}

function runMenuItem(entry: AxisCellMenuItemEntry, handlers: AxisCellMenuHandlers): void {
  switch (entry.item) {
    case AxisCellMenuItem.RENAME_DIM:
      handlers.renameDim();
      break;
    case AxisCellMenuItem.TOGGLE_HEADER:
      handlers.toggleHeader();
      break;
    case AxisCellMenuItem.DELETE_TABLE:
      handlers.deleteTable();
      break;
    case AxisCellMenuItem.DELETE_DIM:
      if (entry.range != null) {
        handlers.deleteDim(entry.range);
      }
      break;
    case AxisCellMenuItem.LOCK_COLUMN:
      handlers.setColumnProtected(true);
      break;
    case AxisCellMenuItem.UNLOCK_COLUMN:
      handlers.setColumnProtected(false);
      break;
    case AxisCellMenuItem.FREEZE_COLUMN:
      handlers.freezeColumn();
      break;
    case AxisCellMenuItem.UNFREEZE_COLUMN:
      handlers.unfreezeColumn();
      break;
    case AxisCellMenuItem.INSERT_DIM_BEFORE:
    case AxisCellMenuItem.INSERT_DIM_AFTER:
      if (entry.range != null) {
        handlers.insertDim(getInsertDirection(entry.item), entry.range, {});
      }
      break;
    default:
      assert(false, `Unexpected context menu item ${entry.item}`);
  }
}

function toContextMenuOption(
  entry: AxisCellMenuEntry,
  handlers: AxisCellMenuHandlers
): contextmenu.ContextMenuOption {
  if (entry.kind === 'separator') {
    return '-';
  }
  if (entry.kind === 'extra') {
    return entry.option;
  }
  return {
    name: entry.name,
    hide: entry.isHidden,
    disabled: entry.isDisabled,
    tooltip: entry.tooltip,
    ...(entry.dataTestAttrs != null && {dataTestAttrs: entry.dataTestAttrs}),
    ...(entry.isClickable && {
      click: () => {
        runMenuItem(entry, handlers);
      },
    }),
    ...(isInsertItem(entry.item) &&
      entry.isClickable && {
        menu:
          entry.insertOptions?.map((option) => ({
            name: option.name,
            dataTestAttrs: option.dataTestAttrs,
            click: () => {
              if (entry.range != null) {
                handlers.insertDim(getInsertDirection(entry.item), entry.range, option.columnInfo);
              }
            },
          })) ?? null,
      }),
  };
}

/**
 * Builds the axis cell's context menu: {@link computeAxisCellContextMenu} decides which options the
 * menu lists, how they read and which of them are hidden or disabled, and this attaches the handler
 * for each of them.
 *
 * {@link useAxisCellContextMenu} is what the rendered cell calls; this takes the cell's model and
 * analytics parameters directly so the wiring can be exercised without rendering.
 */
export function getAxisCellContextMenuOptions(
  props: TableAxisCellProps,
  dimActions: AxisCellDimActions,
  axisCellModel: AxisCellModel,
  tableContext: AxisCellContextMenuTableContext = NO_TABLE_CONTEXT,
  analyticsParams?: AnalyticsParams | null
): Array<contextmenu.ContextMenuOption> {
  const entries = computeAxisCellContextMenu(
    getContextMenuInput(props, dimActions, axisCellModel, tableContext)
  );
  const handlers = getContextMenuHandlers(props, dimActions, axisCellModel, tableContext, analyticsParams);
  return entries.map((entry) => toContextMenuOption(entry, handlers));
}

/**
 * The context menu inputs the table contributes to the axis cell at (`dim`, `selectionRange`).
 *
 * Each axis has its own set of extra options, and only the column axis can be renamed, so only it
 * needs the disallowed column names. The column axis's options are additionally given the
 * `setColumnConstraint` action they need to change a column's type.
 */
export function useAxisCellContextMenuTableContext(
  axisCellModel: AxisCellModel,
  mutations: TableMutationMethods
): AxisCellContextMenuTableContext {
  const {setColumnConstraint} = mutations;
  const {dim, selectionRange} = axisCellModel;
  const getExtraColumnOptions = useTableProps((props) => props.getExtraContextMenuOptions);
  const getExtraRowOptions = useTableProps((props) => props.getExtraRowAxisContextMenuOptions);
  const getDisallowedColumnNames = useTableProps((props) => props.getDisallowedColumnNames);
  const isSingleInputView = useTableProps((props) => props.isSingleInputView ?? false);

  const extraOptions =
    dim === TABLE_ROW
      ? getExtraRowOptions?.(selectionRange)
      : getExtraColumnOptions?.(selectionRange, setColumnConstraint);

  return {
    extraOptions: extraOptions ?? [],
    getDisallowedColumnNames: dim === TABLE_ROW ? undefined : getDisallowedColumnNames,
    isSingleInputView,
    setColumnConstraint,
  };
}

/**
 * The context menu of the axis cell this is rendered in: the options it lists, and whether it
 * offers a menu at all.
 *
 * `tableContext` is passed in rather than read here because an axis cell rendered outside of a
 * table (`SimpleTableAxisCell`) has no table store to read it from; `TableAxisCell` reads it with
 * {@link useAxisCellContextMenuTableContext} and hands it down.
 */
export function useAxisCellContextMenu(
  props: TableAxisCellProps,
  dimActions: AxisCellDimActions | undefined,
  tableContext: AxisCellContextMenuTableContext | undefined
): {menuOptions: Array<contextmenu.ContextMenuOption>; hasContextOptions: boolean} {
  const axisCellModel = useAxisCellModel();
  const {rootFolderId: projectId, fileId, templateCollectionId} = EditorPropertiesContext.useContext();
  const {structuredTableKind, provenanceUuid} = StructuredTableContext.useContext();

  const analyticsParams: AnalyticsParams = {
    structuredTableAnalyticsParams:
      structuredTableKind != null
        ? {structuredTableKind, structuredTableProvenanceUuid: provenanceUuid!}
        : null,
    fileAnalyticsParams: {fileId, projectId, templateCollectionId},
  };

  const menuOptions = getAxisCellContextMenuOptions(
    props,
    dimActions ?? NO_DIM_ACTIONS,
    axisCellModel,
    tableContext ?? NO_TABLE_CONTEXT,
    analyticsParams
  );

  return {
    menuOptions,
    hasContextOptions: hasVisibleContextOptions(axisCellModel.isGhostCell, menuOptions),
  };
}
